import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TextField, Button, Box, Typography, Alert, AlertTitle, Paper, CircularProgress } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { Container } from '~/components/ui';
import { BarcodeInput } from '~/components/BarcodeInput';
import { PhoneInput } from '~/components/PhoneInput';
import { DateInput } from '~/components/DateInput';
import { validateBarcode as validateBarcodeClient } from '../../lib/barcode-validator';
import { createCustomer } from '~/lib/api/api-client';
import { useDevice } from '~/lib/auth/device-context';
import { queue } from '~/components/SnackbarQueue';

export default function Register() {
  const [searchParams] = useSearchParams();
  const suggestedBarcode = searchParams.get("barcode") || "";
  const suggestedPhone = searchParams.get("phone") || "";
  const navigate = useNavigate();
  const { deviceToken } = useDevice();

  const [barcode, setBarcode] = useState(suggestedBarcode);
  const [phone, setPhone] = useState(suggestedPhone);
  const [name, setName] = useState("");
  const [birthdate, setBirthdate] = useState("");
  const [barcodeError, setBarcodeError] = useState("");
  const [barcodeValid, setBarcodeValid] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [redirectTimer, setRedirectTimer] = useState<number | null>(null);
  const [registeredCustomer, setRegisteredCustomer] = useState<any>(null);

  // Validate pre-filled barcode on mount
  useEffect(() => {
    if (suggestedBarcode) {
      const validation = validateBarcodeClient(suggestedBarcode);
      setBarcodeValid(validation.valid);
      if (!validation.valid) {
        setBarcodeError('الرجاء التحقق من البيانات المدخلة');
      }
    }
  }, [suggestedBarcode]);

  // Auto-redirect countdown after successful registration
  useEffect(() => {
    if (redirectTimer === null) return;
    
    if (redirectTimer <= 0) {
      if (registeredCustomer) {
        navigate(`/?barcode=${registeredCustomer.barcode}`);
      }
      return;
    }
    
    const timer = setTimeout(() => {
      setRedirectTimer(redirectTimer - 1);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [redirectTimer, registeredCustomer, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setBarcodeError("");

    if (!barcode) {
      setBarcodeError("الرجاء التحقق من البيانات المدخلة");
      return;
    }

    const validation = validateBarcodeClient(barcode);
    if (!validation.valid) {
      setBarcodeError("الرجاء التحقق من البيانات المدخلة");
      return;
    }

    if (!deviceToken) {
      setError("الجهاز غير مسجل");
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await createCustomer({
        barcode,
        name,
        phone,
        deviceToken
      });

      if (result.success) {
        queue.notify({
          title: '✓ تم تسجيل العميل بنجاح',
          body: `تم تسجيل ${result.customer.name}`,
          timeout: 3000,
        });
        
        // Store customer and start 10-second countdown
        setRegisteredCustomer(result.customer);
        setRedirectTimer(10);
      } else {
        setError(result.error || "حدث خطأ أثناء التسجيل");
      }
    } catch (err: any) {
      setError(err.message || "حدث خطأ أثناء التسجيل");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNameChange = useCallback((e: any) => setName(e.target.value), []);

  return (
    <Container size="xl" padding>
      <Box sx={{ py: 2 }}>
        <Button
          onClick={() => navigate(-1)}
          startIcon={<ArrowForward />}
          variant="text"
        >
          رجوع
        </Button>
      </Box>

      <Paper
        elevation={2}
        sx={{
          p: 4,
          borderRadius: 3,
          maxWidth: 600,
          mx: 'auto',
        }}
      >
        <Typography variant="h4" component="h1" sx={{ mb: 4, fontWeight: 600 }}>
          تسجيل عميل جديد
        </Typography>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              الباركود <span style={{ color: 'error.main' }}>*</span>
            </Typography>
            <BarcodeInput
              value={barcode}
              onChange={(value) => {
                setBarcode(value);
                const validation = validateBarcodeClient(value);
                setBarcodeValid(validation.valid);
                if (!validation.valid && value) {
                  setBarcodeError('الرجاء التحقق من البيانات المدخلة');
                } else {
                  setBarcodeError('');
                }
              }}
              disabled={isSubmitting}
              error={!!barcodeError}
              helperText={barcodeError}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              الاسم <span style={{ color: 'error.main' }}>*</span>
            </Typography>
            <TextField
              value={name}
              onChange={handleNameChange}
              name="name"
              autoComplete="name"
              placeholder="أحمد محمد"
              required
              disabled={isSubmitting}
              fullWidth
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              رقم الهاتف <span style={{ color: 'error.main' }}>*</span>
            </Typography>
            {suggestedPhone && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                رقم الهاتف المسجل مسبقاً
              </Typography>
            )}
            <PhoneInput
              value={phone}
              onChange={setPhone}
              disabled={isSubmitting}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              تاريخ الميلاد
            </Typography>
            <DateInput
              value={birthdate}
              onChange={setBirthdate}
              disabled={isSubmitting}
            />
          </Box>

          {error && (
            <Alert severity="error">
              <AlertTitle>خطأ</AlertTitle>
              {error}
            </Alert>
          )}

          {registeredCustomer && redirectTimer !== null && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <AlertTitle>✓ تم التسجيل بنجاح</AlertTitle>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                تم تسجيل {registeredCustomer.name} بنجاح
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  الانتقال لصفحة المعاملات بعد {redirectTimer} ثانية...
                </Typography>
                <Button 
                  size="small" 
                  variant="outlined"
                  onClick={() => navigate(`/?barcode=${registeredCustomer.barcode}`)}
                >
                  بدء معاملة الآن
                </Button>
              </Box>
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={
              isSubmitting || !!barcodeError || !barcode || !barcodeValid || phone.length !== 11
            }
            fullWidth
            sx={{ height: 56, mt: 2 }}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {isSubmitting ? 'جاري التسجيل...' : 'تسجيل العميل'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}
